import { Injectable } from '@angular/core';
import * as moment from 'moment';

export interface IDurationObj {
  value: number;
  unit: string;
  abbr: string;
}

@Injectable({
  providedIn: 'root'
})
export class DateFormatterService {
  static formats: any = {
    DateTime: 'YYYY-MM-DD HH:mm',
    Date: 'YYYY-MM-DD',
    Time: 'HH:mm'
  };

  static highchartsFormats = '%b %d, %Y %H:%M';

  getDateTime(timeStamp: any, format?: string): string {
    return moment(timeStamp).format(format || DateFormatterService.formats.DateTime);
  }

  getUTCDateTime(timeStamp: any, format?: string): string {
    return moment.utc(timeStamp).format(format || DateFormatterService.formats.DateTime);
  }

  getDate(timeStamp: any, format?: string): string {
    return moment(timeStamp).format(format || DateFormatterService.formats.Date);
  }

  getTime(timeStamp: any, format?: string): string {
    return moment(timeStamp).format(format || DateFormatterService.formats.Time);
  }

  //eslint-disable-next-line
  getLocaleDateString(timestamp: number) {
    const localeOptions: any = { dateStyle: 'medium', hour12: false };
    return new Date(timestamp).toLocaleString('en', localeOptions);
  }

  // eslint-disable-next-line
  getLocaleDateFromTimestamp(dateTimeString: any): string {
    const timestamp = this.getTimestampFromYYYYMMDDHHMMSS(dateTimeString);
    return this.getLocaleDateString(timestamp);
  }

  getTimestampFromYYYYMMDDHHMMSS(dateTimeString: any): number {
    const year = parseInt(dateTimeString.substr(0, 4));

    const month = parseInt(dateTimeString.substr(4, 2)) - 1; // JavaScript months are 0-indexed

    const day = parseInt(dateTimeString.substr(6, 2));

    const hour = parseInt(dateTimeString.substr(8, 2));

    const minute = parseInt(dateTimeString.substr(10, 2));

    const second = parseInt(dateTimeString.substr(12, 2));

    const date = new Date(year, month, day, hour, minute, second);

    return date.getTime(); // Returns timestamp in milliseconds
  }

  getDurationPresentation(timestamp) {
    const durationObj = this.getDurationObj(timestamp);
    return `${durationObj.value} ${durationObj.unit}`;
  }

  getCalendarTime(timestamp) {
    // see https://momentjs.com/docs/#/displaying/calendar-time/
    return moment(timestamp).calendar(null, {
      sameDay: `[Today at] ${DateFormatterService.formats.Time}`,
      lastDay: `[Yesterday at] ${DateFormatterService.formats.Time}`,
      lastWeek: `[Last] dddd [at] ${DateFormatterService.formats.Time}`,
      sameElse: DateFormatterService.formats.DateTime
    });
  }

  getDurationObj(timestamp): IDurationObj {
    const days = Math.floor(timestamp / 86400000);
    timestamp = timestamp % 86400000;
    const hours = Math.floor(timestamp / 3600000);
    timestamp = timestamp % 3600000;
    const minutes = Math.floor(timestamp / 60000);
    timestamp = timestamp % 60000;
    const seconds = Math.floor(timestamp / 1000);
    const dayUnit = days > 1 ? 'days' : 'day';
    const hourUnit = hours > 1 ? 'hours' : 'hour';
    return (
      this.getValueUnitObj(days, dayUnit, 'd') ||
      this.getValueUnitObj(hours, hourUnit, 'hr') ||
      this.getValueUnitObj(minutes, 'min', 'm') ||
      this.getValueUnitObj(seconds, 'sec', 's')
    );
  }

  getTimestamp(date: string) {
    return moment(date).valueOf();
  }

  // returns the client timezone offset (in minutes)
  getTimezoneOffset() {
    return new Date().getTimezoneOffset();
  }

  private getValueUnitObj(value, unit, abbr): IDurationObj {
    return value && { value, unit, abbr };
  }

  //Fusion returns with UTC time format 'YYYYMMDDHHSS'. Just format based on display.
  getDateFromTimestamp(input: string): string {
    const dateString =
      input.substring(4, 6) + '/' + input.substring(6, 8) + '/' + input.substring(0, 4);
    return dateString;
  }

  getTimeFromTimestamp(input: string): string {
    const timeString =
      input.substring(8, 10) + ':' + input.substring(10, 12) + ':' + input.substring(12, 14);
    return timeString;
  }

  getISODateString(input: string): string {
    return input && input.length >= 12
      ? `${input.substring(0, 4)}-${input.substring(4, 6)}-${input.substring(
          6,
          8
        )}T${input.substring(8, 10)}:${input.substring(10, 12)}Z`
      : null;
  }
}
